import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { policiesApi } from "@/api/endpoints";
import type { Policy } from "@/api/types";
import { useAuth } from "@/auth/AuthContext";
import { canAccess } from "@/auth/permissions";
import { NewPolicyModal } from "@/components/creates/NewPolicyModal";
import { PageHeader } from "@/components/PageHeader";
import { SavedViewsBar } from "@/components/SavedViewsBar";
import { POLICY_STATUSES } from "@/constants/crm";
import { formatCurrency, formatDate, statusClass } from "@/utils/format";

const inputStyle = {
  padding: "0.55rem 0.7rem",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--surface, #fff)",
};

export function PoliciesPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<string | null>("expiration_date:asc");
  const [items, setItems] = useState<Policy[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    policiesApi
      .query({ q: query || undefined, status: status || undefined, sort: sort || undefined, limit: 100 })
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load policies"));
  }, [query, status, sort, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  function toggleSort(field: string) {
    setSort((prev) => (prev === `${field}:desc` ? `${field}:asc` : `${field}:desc`));
  }
  function arrow(field: string) {
    if (sort === `${field}:desc`) return " ↓";
    if (sort === `${field}:asc`) return " ↑";
    return "";
  }

  const currentFilters: Record<string, string> = {};
  if (status) currentFilters.status = status;
  if (query) currentFilters.q = query;

  return (
    <>
      <PageHeader
        title="Policies"
        subtitle="In-force policies, premiums, and renewals."
        actions={
          canAccess(user, "crm.edit") ? (
            <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + New policy
            </button>
          ) : null
        }
      />
      <NewPolicyModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          setRefreshKey((k) => k + 1);
        }}
      />

      <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search policy number or product line…"
          style={{ ...inputStyle, flex: 1, minWidth: 220 }}
        />
        <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {POLICY_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`btn ${sort === "premium_cents:desc" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setSort("premium_cents:desc")}
        >
          Top by premium
        </button>
      </div>

      <div style={{ marginBottom: "0.6rem" }}>
        <SavedViewsBar
          entity="policies"
          filters={currentFilters}
          sort={sort}
          onApply={(f, s) => {
            setStatus(f.status ?? "");
            setQuery(f.q ?? "");
            setSort(s);
          }}
        />
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="card">
        <div style={{ padding: "0.5rem 0.25rem", color: "var(--muted)", fontSize: "0.85rem" }}>{total} policies</div>
        {items.length === 0 ? (
          <div className="empty-state">No policies found.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Policy #</th>
                <th>Product</th>
                <th>Status</th>
                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("premium_cents")}>
                  Premium{arrow("premium_cents")}
                </th>
                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("expiration_date")}>
                  Expires{arrow("expiration_date")}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((policy) => (
                <tr key={policy.id}>
                  <td>
                    <Link to={`/policies/${policy.id}`}>{policy.policy_number}</Link>
                  </td>
                  <td>{policy.product_line ?? "—"}</td>
                  <td>
                    <span className={statusClass(policy.status)}>{policy.status}</span>
                  </td>
                  <td>{policy.premium_cents ? formatCurrency(policy.premium_cents) : "—"}</td>
                  <td>{policy.expiration_date ? formatDate(policy.expiration_date) : "—"}</td>
                  <td>
                    {policy.customer_id ? (
                      <Link to={`/clients/${policy.customer_id}`} className="btn btn-secondary">
                        Client
                      </Link>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
